import React from "react";
import { PolicySummary } from "../../api/services";

/**
 * Renewal-concierge test mode.
 *
 * Visiting `/renewal/test` loads a fixture policy instead of resolving a real
 * `nextTerm.uuid`, so the whole flow can be clicked through without seeding
 * data. The sentinel is matched on the route param, so no env var is needed
 * to demo it — but a banner makes it obvious the data isn't real.
 *
 * Nothing here is reachable from a real uuid: `TEST_TERM_UUID` is not a valid
 * UUID, so it can never collide with a genuine term.
 */
export const TEST_TERM_UUID = "test";

export const isRenewalTestMode = (termUuid: string | undefined): boolean =>
  termUuid === TEST_TERM_UUID;

/** Lease end ~90 days out, so the early/late move-out notices are exercisable. */
const daysFromNow = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

/**
 * Fixture shaped like a real `PolicySummary`. Cast at the edge because the
 * account rows carry contact fields the tenant API doesn't return yet — they
 * let the PM contact card render fully in the demo.
 */
export const buildTestPolicy = (): PolicySummary =>
  ({
    id: 0,
    uuid: "00000000-0000-0000-0000-0000000000p0",
    createdAt: new Date().toISOString(),
    name: "Test Policy",
    status: "covered",
    coverageAmount: 3525,
    startsOn: daysFromNow(-275),
    endsOn: daysFromNow(90),
    renewalTerm: "fixed",
    unit: {
      id: 0,
      uuid: "00000000-0000-0000-0000-0000000000u0",
      name: "Unit 4B",
      property: {
        id: 0,
        uuid: "00000000-0000-0000-0000-0000000000r0",
        name: "Maple Street Apartments",
        address: "1234 Maple Street, Carson City, NV 89701",
      },
    },
    account: {
      id: 0,
      uuid: "00000000-0000-0000-0000-0000000000a0",
      companyName: "Realty Boulevard",
      phone: "(555) 867-5309",
      email: "renewals@realtyboulevard.example",
      website: "realtyboulevard.example",
    },
    currentTerm: {
      uuid: "00000000-0000-0000-0000-0000000000c0",
      coverageAmount: 3525,
      monthlyRentAmount: 1750,
      startsOn: daysFromNow(-275),
      endsOn: daysFromNow(90),
      status: "active",
      renewalTerm: "fixed",
      total: 801.54,
    },
    nextTerm: {
      uuid: TEST_TERM_UUID,
      coverageAmount: 3525,
      monthlyRentAmount: 1800,
      startsOn: daysFromNow(91),
      endsOn: daysFromNow(456),
      status: "scheduled",
      renewalTerm: "fixed",
      total: 824.9,
    },
  }) as unknown as PolicySummary;

export const RenewalTestModeBanner: React.FC<{ termUuid?: string }> = ({
  termUuid,
}) => {
  if (!isRenewalTestMode(termUuid)) return null;
  return (
    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs font-medium text-amber-800">
      Test mode — sample renewal data. Nothing is saved.
    </div>
  );
};
